import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import EntityMap from "../components/EntityMap";
import { Loader2, UserPlus, Phone, Mail, MapPin, Handshake, CheckCircle2, Clock, Map as MapIcon, List } from "lucide-react";

const STATUS_OPTIONS = ["Da contattare", "In lavorazione", "Non idoneo", "Convertito"];
const ESP_LABEL = { commerciale: "Commerciale", installatore: "Installatore", agenzia: "Agenzia", altro: "Altro" };

const statusColor = (s) => ({
  "Da contattare": "border-amber-500/40 bg-amber-500/10 text-amber-600",
  "In lavorazione": "border-blue-500/40 bg-blue-500/10 text-blue-600",
  "Non idoneo": "border-red-500/40 bg-red-500/10 text-red-600",
  "Convertito": "border-emerald-500/40 bg-emerald-500/10 text-emerald-600",
}[s] || "border-border bg-secondary text-muted-foreground");

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso || ""; } };

export default function PartnerCandidates() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState(null);
  const [activity, setActivity] = useState("");
  const [busy, setBusy] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [conv, setConv] = useState({ email: "", name: "", commission_pct: 0 });
  const [tempPwd, setTempPwd] = useState(null);
  const [view, setView] = useState("list");
  const [geo, setGeo] = useState(null);
  const [geoBusy, setGeoBusy] = useState(false);

  const loadGeo = useCallback(async () => {
    try {
      const { data } = await api.get("/partner-candidates-geo");
      setGeo(data);
    } catch (e) { setGeo({ points: [], pending: 0 }); }
  }, []);

  useEffect(() => { if (view === "map") loadGeo(); }, [view, loadGeo]);

  const runGeocoding = async () => {
    setGeoBusy(true);
    try {
      const { data } = await api.post("/partner-candidates-geo/geocode-pending?limit=40");
      toast.success(`Geolocalizzate ${data.ok} province · ${data.remaining} in attesa`);
      await loadGeo();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGeoBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partner-candidates", { params: filter === "all" ? {} : { status: filter } });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = useCallback(async (cid) => {
    try {
      const { data } = await api.get(`/partner-candidates/${cid}`);
      setSel(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);

  useEffect(() => { if (id) openDetail(id); }, [id, openDetail]);

  const closeDetail = () => { setSel(null); if (id) navigate("/candidati-partner"); };

  const changeStatus = async (status) => {
    if (!sel) return;
    try {
      const { data } = await api.patch(`/partner-candidates/${sel.id}`, { status });
      setSel(data); load();
      toast.success("Stato aggiornato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addActivity = async () => {
    if (!activity.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/partner-candidates/${sel.id}/activity`, { note: activity.trim() });
      setSel(data); setActivity("");
      toast.success("Attività registrata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openConvert = () => {
    setConv({ email: sel.email || "", name: sel.nome || "", commission_pct: 0 });
    setTempPwd(null);
    setConvertOpen(true);
  };

  const doConvert = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/partner-candidates/${sel.id}/convert`, {
        email: conv.email, name: conv.name, commission_pct: Number(conv.commission_pct) || 0,
      });
      setTempPwd(data.temp_password);
      toast.success("Candidato convertito in Partner Commerciale");
      openDetail(sel.id); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="partner-candidates-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-extrabold flex items-center gap-2"><UserPlus className="h-6 w-6 text-primary" /> Candidati Partner Commerciali</h1>
          <p className="text-sm text-muted-foreground">Candidature ricevute dalla landing del configuratore.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-border p-1 bg-secondary/30" data-testid="candidates-view-switch">
            <button type="button" onClick={() => setView("list")} data-testid="candidates-view-list"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              <List className="h-4 w-4" /> Elenco
            </button>
            <button type="button" onClick={() => setView("map")} data-testid="candidates-view-map"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${view === "map" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              <MapIcon className="h-4 w-4" /> Mappa
            </button>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-52" data-testid="candidate-filter-status"><SelectValue placeholder="Stato" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {view === "map" && (
        <Card className="rounded border-border p-4" data-testid="candidates-map-card">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <h3 className="font-heading font-semibold text-sm flex items-center gap-2"><MapIcon className="h-4 w-4 text-primary" strokeWidth={1.5} /> Copertura per provincia ({geo?.points?.length ?? 0} province)</h3>
            <div className="flex items-center gap-2">
              {geo?.pending ? <span className="text-xs text-amber-600" data-testid="candidates-geo-pending">{geo.pending} da geolocalizzare</span> : null}
              <Button variant="outline" size="sm" onClick={runGeocoding} disabled={geoBusy} data-testid="candidates-geocode-btn">
                {geoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Geolocalizza${geo?.pending ? ` (${geo.pending})` : ""}`}
              </Button>
            </div>
          </div>
          {geo?.points?.length ? (
            <EntityMap
              points={geo.points.map((p) => ({
                id: p.provincia, lat: p.lat, lon: p.lon,
                color: p.da_contattare > 0 ? "#f59e0b" : "#10b981",
                title: `${p.provincia} — ${p.count} candidat${p.count === 1 ? "o" : "i"}${p.da_contattare ? ` · ${p.da_contattare} da contattare` : ""}${p.convertiti ? ` · ${p.convertiti} convertiti` : ""}`,
              }))}
              height={440}
            />
          ) : (
            <div className="p-10 text-center text-sm text-muted-foreground" data-testid="candidates-map-empty">
              {geo?.pending ? "Nessuna provincia ancora geolocalizzata. Premi \"Geolocalizza\" per popolare la mappa." : "Nessun candidato da mostrare sulla mappa."}
            </div>
          )}
          <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#f59e0b" }} /> Con candidati da contattare</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#10b981" }} /> Tutti gestiti/convertiti</span>
          </div>
        </Card>
      )}

      {view === "list" && (
      <Card className="rounded border-border overflow-hidden">
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground" data-testid="candidates-empty">Nessun candidato.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Candidato</TableHead>
                <TableHead>Provincia</TableHead>
                <TableHead>Esperienza</TableHead>
                <TableHead>Contatti</TableHead>
                <TableHead>Ricevuta</TableHead>
                <TableHead>Stato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} className="cursor-pointer" onClick={() => openDetail(c.id)} data-testid={`candidate-row-${c.id}`}>
                  <TableCell className="font-medium">{c.nome}{c.azienda ? <span className="block text-xs text-muted-foreground">{c.azienda}</span> : null}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.provincia}</TableCell>
                  <TableCell className="text-sm">{ESP_LABEL[c.esperienza] || c.esperienza}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.telefono}<span className="block text-xs">{c.email}</span></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(c.created_at)}</TableCell>
                  <TableCell><span className={`inline-flex items-center rounded-full border text-[11px] font-semibold px-2 py-0.5 ${statusColor(c.status)}`}>{c.status}</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      )}

      {/* DETTAGLIO */}
      <Dialog open={!!sel} onOpenChange={(v) => { if (!v) closeDetail(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="candidate-detail-dialog">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">{sel.nome}
                  <span className={`inline-flex items-center rounded-full border text-[11px] font-semibold px-2 py-0.5 ${statusColor(sel.status)}`}>{sel.status}</span>
                </DialogTitle>
                <DialogDescription>{sel.azienda || "Candidato Partner Commerciale"}</DialogDescription>
              </DialogHeader>

              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" /> {sel.provincia}</div>
                <div className="flex items-center gap-2"><Handshake className="h-4 w-4 text-muted-foreground" /> {ESP_LABEL[sel.esperienza] || sel.esperienza}</div>
                <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /> {sel.telefono}</div>
                <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /> {sel.email}</div>
              </div>
              {sel.messaggio ? <div className="rounded-md border border-border bg-secondary/30 p-3 text-sm"><span className="text-xs text-muted-foreground">Messaggio</span><p>{sel.messaggio}</p></div> : null}
              {sel.next_task && !sel.converted ? (
                <div className="flex items-center gap-2 text-sm text-amber-600" data-testid="candidate-next-task"><Clock className="h-4 w-4" /> {sel.next_task.label}</div>
              ) : null}

              <div className="flex items-center gap-2 flex-wrap">
                <Select value={sel.status} onValueChange={changeStatus} disabled={sel.converted}>
                  <SelectTrigger className="w-48" data-testid="candidate-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                {!sel.converted ? (
                  <Button onClick={openConvert} data-testid="candidate-convert-btn"><UserPlus className="h-4 w-4 mr-1.5" /> Converti in Partner Commerciale</Button>
                ) : (
                  <span className="inline-flex items-center gap-1 text-sm text-emerald-600" data-testid="candidate-converted-flag"><CheckCircle2 className="h-4 w-4" /> Convertito</span>
                )}
              </div>

              {/* Timeline */}
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Storico attività</div>
                <div className="space-y-2 max-h-52 overflow-y-auto">
                  {(sel.timeline || []).slice().reverse().map((t) => (
                    <div key={t.id} className="rounded-md border border-border p-2.5 text-sm" data-testid={`candidate-activity-${t.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{t.type === "task" ? "Attività" : "Nota"}{t.created_by_name ? ` · ${t.created_by_name}` : ""}</span>
                        <span className="text-[11px] text-muted-foreground">{fmtDate(t.created_at)}</span>
                      </div>
                      <p className="text-muted-foreground">{t.note}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-start gap-2">
                  <Textarea value={activity} onChange={(e) => setActivity(e.target.value)} rows={2} placeholder="Registra una nota / esito contatto..." data-testid="candidate-activity-input" />
                  <Button onClick={addActivity} disabled={busy || !activity.trim()} data-testid="candidate-activity-add">Aggiungi</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* CONVERSIONE */}
      <Dialog open={convertOpen} onOpenChange={(v) => { setConvertOpen(v); if (!v) setTempPwd(null); }}>
        <DialogContent className="max-w-md" data-testid="candidate-convert-dialog">
          {!tempPwd ? (
            <>
              <DialogHeader>
                <DialogTitle>Converti in Partner Commerciale</DialogTitle>
                <DialogDescription>Verrà creato un account con login. Al partner arriverà un'email con la password temporanea.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div><label className="text-xs text-muted-foreground">Nome</label><Input value={conv.name} onChange={(e) => setConv((s) => ({ ...s, name: e.target.value }))} data-testid="convert-name" /></div>
                <div><label className="text-xs text-muted-foreground">Email (login)</label><Input type="email" value={conv.email} onChange={(e) => setConv((s) => ({ ...s, email: e.target.value }))} data-testid="convert-email" /></div>
                <div><label className="text-xs text-muted-foreground">Provvigione %</label><Input type="number" value={conv.commission_pct} onChange={(e) => setConv((s) => ({ ...s, commission_pct: e.target.value }))} data-testid="convert-commission" /></div>
              </div>
              <DialogFooter>
                <Button onClick={doConvert} disabled={busy} data-testid="convert-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea account partner"}</Button>
              </DialogFooter>
            </>
          ) : (
            <div className="py-4 text-center" data-testid="convert-success">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
              <h3 className="font-heading text-lg font-bold">Partner creato!</h3>
              <p className="text-sm text-muted-foreground mt-1">Account: <b>{conv.email}</b></p>
              <div className="mt-3 rounded-md border border-border bg-secondary/40 p-3 text-sm">Password temporanea:<br /><b className="text-base tracking-wide" data-testid="convert-temp-password">{tempPwd}</b></div>
              <p className="text-[11px] text-muted-foreground mt-2">Comunicala al partner (inviata anche via email).</p>
              <Button variant="outline" className="mt-4" onClick={() => setConvertOpen(false)} data-testid="convert-close">Chiudi</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
